import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Chat,
  Report,
  Logout,
  Home,
  CardGiftcard,
  Favorite,
  ChevronRight,
  AdminPanelSettings,
  RateReview,
  HelpOutline,
  Gavel,
  QuestionAnswer,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

// ─── Design tokens ──────────────────────────────────────────────────
const SIDEBAR_BG     = '#1A0A2E';
const SIDEBAR_W      = 240;
const PINK           = '#EC4899';
const CORAL          = '#FF6B35';
const ITEM_HOVER     = 'rgba(236,72,153,0.10)';
const ITEM_ACTIVE_BG = 'rgba(236,72,153,0.18)';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl]     = useState<null | HTMLElement>(null);
  const { user, logout }            = useAuth();
  const navigate                    = useNavigate();
  const location                    = useLocation();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen  = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout    = async () => { await logout(); navigate('/'); handleMenuClose(); };

  const menuItems = [
    { text: '대시보드',   icon: <Dashboard />,      path: '/admin' },
    { text: '사용자 관리', icon: <People />,         path: '/admin/users' },
    { text: '큐피트 카드', icon: <CardGiftcard />,   path: '/admin/cupid-cards' },
    { text: '채팅방 관리', icon: <Chat />,           path: '/admin/chats' },
    { text: '신고 관리',   icon: <Report />,         path: '/admin/reports' },
    { text: '프로필 심사', icon: <RateReview />,     path: '/admin/profile-reviews' },
    { text: '문의 관리',   icon: <HelpOutline />,    path: '/admin/inquiries' },
    { text: '이의제기',    icon: <Gavel />,          path: '/admin/appeals' },
    { text: 'FAQ 관리',    icon: <QuestionAnswer />, path: '/admin/faqs' },
  ];

  const isActive = (path: string) =>
    path === '/admin'
      ? location.pathname === '/admin'
      : location.pathname.startsWith(path);

  const adminName = (user as any)?.name || user?.userName || 'Admin';
  const adminEmail = (user as any)?.email || '';
  const initials = adminName.slice(0, 2).toUpperCase();

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', background: SIDEBAR_BG, color: '#fff' }}>

      {/* ── Brand ── */}
      <Box sx={{ px: 3, py: 3, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px',
            background: `linear-gradient(135deg, ${PINK}, ${CORAL})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            boxShadow: `0 4px 14px ${PINK}55`,
          }}>
            <Favorite sx={{ fontSize: 18, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '0.95rem', color: '#fff', lineHeight: 1.1 }}>
              Silver Connect
            </Typography>
            <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', fontWeight: 500, letterSpacing: 2 }}>
              ADMIN PANEL
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Nav items ── */}
      <List sx={{ flex: 1, px: 1.5, pt: 2, overflowY: 'auto' }}>
        {menuItems.map((item) => {
          const active = isActive(item.path);
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: '10px',
                  px: 1.5, py: 1,
                  position: 'relative',
                  background: active ? ITEM_ACTIVE_BG : 'transparent',
                  '&:hover': { background: active ? ITEM_ACTIVE_BG : ITEM_HOVER },
                  transition: 'background 0.15s ease',
                }}
              >
                {active && (
                  <Box sx={{
                    position: 'absolute',
                    left: 0, top: '50%', transform: 'translateY(-50%)',
                    width: 3, height: '60%', borderRadius: '0 3px 3px 0',
                    background: `linear-gradient(180deg, ${PINK}, ${CORAL})`,
                  }} />
                )}
                <ListItemIcon sx={{
                  minWidth: 36,
                  color: active ? PINK : 'rgba(255,255,255,0.45)',
                  transition: 'color 0.15s',
                  '& svg': { fontSize: 20 },
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: active ? 700 : 500,
                    color: active ? '#fff' : 'rgba(255,255,255,0.6)',
                  }}
                />
                {active && <ChevronRight sx={{ fontSize: 16, color: PINK, opacity: 0.7 }} />}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* ── Footer: back to main ── */}
      <Box sx={{ px: 1.5, pb: 1, borderTop: '1px solid rgba(255,255,255,0.06)', pt: 1.5 }}>
        <ListItemButton
          onClick={() => navigate('/')}
          sx={{ borderRadius: '10px', px: 1.5, py: 0.8, '&:hover': { background: ITEM_HOVER } }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: 'rgba(255,255,255,0.3)' }}>
            <Home sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText
            primary="메인 페이지로"
            primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 500, color: 'rgba(255,255,255,0.4)' }}
          />
        </ListItemButton>
      </Box>

      {/* ── Admin profile card ── */}
      <Box sx={{
        mx: 1.5, mb: 2, mt: 0.5,
        p: 2, borderRadius: '14px',
        background: 'rgba(236,72,153,0.10)',
        border: '1px solid rgba(236,72,153,0.18)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{
            width: 36, height: 36,
            background: `linear-gradient(135deg, ${PINK}, ${CORAL})`,
            fontSize: '0.75rem', fontWeight: 800,
            boxShadow: `0 3px 10px ${PINK}55`,
            flexShrink: 0,
          }}>
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.82rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {adminName}
            </Typography>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.4, mt: 0.3,
              background: `${PINK}33`, px: 0.8, py: 0.15, borderRadius: '4px',
            }}>
              <AdminPanelSettings sx={{ fontSize: 10, color: PINK }} />
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: PINK, letterSpacing: 1 }}>
                ADMIN
              </Typography>
            </Box>
          </Box>
          <Tooltip title="로그아웃">
            <IconButton
              size="small"
              onClick={handleLogout}
              sx={{
                color: 'rgba(255,255,255,0.4)',
                '&:hover': { color: '#FF6B6B', background: 'rgba(255,107,107,0.15)' },
                flexShrink: 0,
              }}
            >
              <Logout sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* ── AppBar ── */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${SIDEBAR_W}px)` },
          ml: { sm: `${SIDEBAR_W}px` },
          background: '#fff',
          borderBottom: '1px solid #F0E8F5',
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, display: { sm: 'none' }, color: SIDEBAR_BG }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            noWrap
            sx={{
              flexGrow: 1,
              fontWeight: 800,
              fontSize: '0.95rem',
              background: `linear-gradient(135deg, ${SIDEBAR_BG} 30%, #7B2D9E)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Silver Connect 관리자
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: SIDEBAR_BG, lineHeight: 1.2 }}>
                {adminName}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: PINK, fontWeight: 600, letterSpacing: 0.5 }}>
                ADMIN
              </Typography>
            </Box>
            <Tooltip title="계정 메뉴">
              <IconButton onClick={handleMenuOpen} size="small">
                <Avatar sx={{
                  width: 34, height: 34,
                  background: `linear-gradient(135deg, ${PINK}, ${CORAL})`,
                  fontSize: '0.75rem', fontWeight: 800,
                  boxShadow: `0 3px 10px ${PINK}44`,
                }}>
                  {initials}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── Sidebar ── */}
      <Box
        component="nav"
        sx={{ width: { sm: SIDEBAR_W }, flexShrink: { sm: 0 } }}
        aria-label="admin navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: SIDEBAR_W, border: 'none' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: SIDEBAR_W, border: 'none' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* ── Main content ── */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${SIDEBAR_W}px)` },
          minHeight: '100vh',
          background: '#F8F4F9',
        }}
      >
        <Toolbar />
        {children}
      </Box>

      {/* ── Account dropdown ── */}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '14px',
            border: '1px solid #F0E8F5',
            boxShadow: '0 8px 32px rgba(26,10,46,0.14)',
            mt: 0.5,
            minWidth: 200,
            overflow: 'hidden',
          },
        }}
      >
        {/* Profile header */}
        <Box sx={{
          px: 2, py: 1.8,
          background: `linear-gradient(135deg, #1A0A2E, #4A1265)`,
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Avatar sx={{
            width: 38, height: 38,
            background: `linear-gradient(135deg, ${PINK}, ${CORAL})`,
            fontSize: '0.78rem', fontWeight: 800,
            flexShrink: 0,
          }}>
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {adminName}
            </Typography>
            {adminEmail && (
              <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {adminEmail}
              </Typography>
            )}
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.4, mt: 0.25,
              background: `${PINK}44`, px: 0.8, py: 0.1, borderRadius: '4px',
            }}>
              <AdminPanelSettings sx={{ fontSize: 10, color: PINK }} />
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: PINK, letterSpacing: 1 }}>ADMIN</Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#F0E8F5' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#C62828',
            gap: 1,
            borderRadius: '8px',
            mx: 0.5,
            my: 0.5,
            '&:hover': { background: '#FFEBEE' },
          }}
        >
          <Logout fontSize="small" />
          로그아웃
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AdminLayout;
